// src/pages/Admin/PagesPersonnalisees/ManageArticlesModal.jsx
import React, { useEffect, useState, useCallback } from "react";
import { FaSave, FaTimes, FaImage, FaEye, FaEyeSlash } from "react-icons/fa";
import {
  getAllArticlesByPage,
  addArticle,
  updateArticle,
  deleteArticle,
} from "./../../../services/articlesPagesServices";
import Button from "./../../../components/Button/Button";
import ButtonEdit from "./../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "react-quill/dist/quill.bubble.css";

// Quill toolbar
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

export default function ManageArticlesModal({ page, onClose }) {
  const [articles, setArticles] = useState([]);
  const [titreArticle, setTitreArticle] = useState("");
  const [contenuTexte, setContenuTexte] = useState("");
  const [urlImage, setUrlImage] = useState("");
  const [ordre, setOrdre] = useState(0);
  const [editingArticle, setEditingArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openPreviewId, setOpenPreviewId] = useState(null);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const resetForm = useCallback(() => {
    setTitreArticle("");
    setContenuTexte("");
    setUrlImage("");
    setOrdre(0);
    setEditingArticle(null);
  }, []);

  const loadArticles = useCallback(async () => {
    if (!page?.id) return;
    setLoading(true);
    try {
      const result = await getAllArticlesByPage(page.id);
      setArticles(Array.isArray(result) ? result : []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des articles.");
    } finally {
      setLoading(false);
    }
  }, [page?.id]);

  useEffect(() => {
    loadArticles();
    resetForm();
  }, [loadArticles, resetForm]);

  const handleSaveArticle = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        page_id: page.id,
        titre_article: titreArticle?.trim(),
        contenu_texte: contenuTexte || "",
        url_image: urlImage || "",
        ordre: Number.isFinite(ordre) ? ordre : 0,
      };

      if (editingArticle) {
        await updateArticle({ id: editingArticle.id, ...payload });
        toast.success("Article mis à jour.");
      } else {
        await addArticle(payload);
        toast.success("Article ajouté.");
      }
      await loadArticles();
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l’enregistrement de l’article.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (article) => {
    setEditingArticle(article);
    setTitreArticle(article.titre_article || "");
    setContenuTexte(article.contenu_texte || "");
    setUrlImage(article.url_image || "");
    setOrdre(Number.isFinite(article.ordre) ? article.ordre : 0);
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await deleteArticle(id);
      toast.success("Article supprimé.");
      await loadArticles();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const togglePreview = (id) => {
    setOpenPreviewId((prev) => (prev === id ? null : id));
  };

  if (!page) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay (clic pour fermer) */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal container – large, scrollable, avec scroll horizontal possible */}
      <div className="relative w-[95vw] max-w-[1200px] overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Articles – <span className="font-normal italic">{page.titre}</span>
          </h3>
          <p className="mt-1 text-center text-white/90 text-xs sm:text-[13px]">
            Ajoutez, modifiez et réorganisez les articles de la page. Le contenu
            peut être long : vous pouvez{" "}
            <span className="underline">scroller horizontalement</span> si
            besoin.
          </p>
        </div>

        {/* Body : vertical + horizontal scrolls */}
        <div className="px-5 sm:px-6 py-5 overflow-auto max-h-[78vh]">
          <div className="min-w-[900px]">
            {/* Formulaire */}
            <form onSubmit={handleSaveArticle}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Titre + ordre */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Titre de l’article
                  </label>
                  <input
                    type="text"
                    placeholder="Titre de l'article"
                    value={titreArticle}
                    onChange={(e) => setTitreArticle(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Ordre
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    value={ordre}
                    onChange={(e) =>
                      setOrdre(parseInt(e.target.value || "0", 10))
                    }
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              {/* Contenu riche */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Contenu
                </label>
                <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                  <ReactQuill
                    value={contenuTexte}
                    onChange={setContenuTexte}
                    modules={modules}
                    formats={formats}
                    className="[&_.ql-container]:min-h-[180px] [&_.ql-container]:max-h-[360px] [&_.ql-container]:overflow-y-auto"
                  />
                </div>
              </div>

              {/* Image + aperçu */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    URL image (optionnel)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                      <FaImage />
                    </span>
                    <input
                      type="text"
                      placeholder="https://…/image.jpg"
                      value={urlImage}
                      onChange={(e) => setUrlImage(e.target.value)}
                      className="w-full pl-9 pr-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>
                </div>

                <div className="flex items-center md:items-start gap-3">
                  <div className="h-16 w-16 rounded-lg overflow-hidden ring-2 ring-slate-200 dark:ring-slate-700 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    {urlImage ? (
                      <img
                        src={urlImage}
                        alt="aperçu"
                        onError={(e) => {
                          e.currentTarget.src = "/img/no-image.jpg";
                        }}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-xs text-slate-500 px-2 text-center">
                        Aperçu
                      </span>
                    )}
                  </div>
                  {!!urlImage && (
                    <span className="text-xs text-slate-500 dark:text-slate-400 break-all">
                      {urlImage}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions formulaire */}
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <Button
                  text={
                    submitting
                      ? "Enregistrement…"
                      : editingArticle
                        ? "Enregistrer modifications"
                        : "Ajouter l’article"
                  }
                  icon={FaSave}
                  type="submit"
                  disabled={submitting}
                  className={`!rounded-xl gap-2 ${submitting ? "opacity-60 pointer-events-none" : ""}`}
                />
                {editingArticle && (
                  <Button
                    text="Annuler modification"
                    icon={FaTimes}
                    onClick={resetForm}
                    type="button"
                    className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
                  />
                )}
              </div>
            </form>

            {/* Séparateur */}
            <div className="my-6 h-px bg-slate-200 dark:bg-slate-800" />

            {/* Liste des articles */}
            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
              <table className="min-w-[900px] w-full">
                <thead>
                  <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                    <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                      Titre
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                      Ordre
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                      Aperçu
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                  {loading && (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-6 text-center text-slate-500 dark:text-slate-400"
                      >
                        Chargement…
                      </td>
                    </tr>
                  )}

                  {!loading && articles.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-6 text-center text-slate-500 dark:text-slate-400"
                      >
                        Aucun article pour le moment.
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    articles.map((a) => (
                      <React.Fragment key={a.id}>
                        <tr className="transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40">
                          <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                            {a.titre_article || "—"}
                          </td>
                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                            {Number.isFinite(a.ordre) ? a.ordre : "—"}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => togglePreview(a.id)}
                              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 px-2 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                            >
                              {openPreviewId === a.id ? (
                                <FaEyeSlash />
                              ) : (
                                <FaEye />
                              )}
                              {openPreviewId === a.id ? "Masquer" : "Voir"}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <ButtonEdit onClick={() => handleEditClick(a)} />
                              <ButtonDelete
                                onClick={() => handleDeleteArticle(a.id)}
                              />
                            </div>
                          </td>
                        </tr>

                        {/* Aperçu en dessous de la ligne */}
                        {openPreviewId === a.id && (
                          <tr>
                            <td colSpan={4} className="px-4 pb-4">
                              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-white/70 dark:bg-slate-900/40">
                                {a.url_image && (
                                  <div className="mb-3">
                                    <img
                                      src={a.url_image}
                                      alt="image article"
                                      onError={(e) => {
                                        e.currentTarget.src =
                                          "/img/no-image.jpg";
                                      }}
                                      className="max-h-40 rounded-lg object-contain ring-1 ring-slate-200 dark:ring-slate-700"
                                    />
                                  </div>
                                )}
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                  <ReactQuill
                                    value={a.contenu_texte || ""}
                                    readOnly
                                    theme="bubble"
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Bouton fermer */}
            <div className="mt-6 flex justify-end">
              <Button
                text="Fermer"
                icon={FaTimes}
                onClick={onClose}
                className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
